import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME } from '../material/ViewerMaterial.js';
import { Vector4 as BABYLON_Vector4, Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { OutlineLayer } from '../OutlineEffect.js';
import { build_bvh_from_mesh } from '../Raycast.js';
export function babylon_change_source_mesh(instance, new_source) {
    if (instance.sourceMesh === new_source)
        return;
    instance.sourceMesh.removeInstance(instance);
    instance._unIndexed = new_source._unIndexed;
    // @ts-expect-error
    instance._sourceMesh = new_source;
    new_source.addInstance(instance);
    instance.refreshBoundingInfo();
    instance._syncSubMeshes();
}
export class SceneAssetMesh {
    constructor(viewer, source) {
        this._source_key_variant_meshes = new Map();
        this._instance_to_active_mesh_key = new Map();
        this._custom_geometry = new Map();
        this.viewer = viewer;
        this._source_mesh = source;
        this.source_material = source.material;
        this.setup_custom_instancing_buffers(this._source_mesh);
        // We need to do this, otherwise we don't get updates when certain things update (such as lights being removed)
        this.viewer.scene.addMesh(this._source_mesh, false);
        if (this.source_material !== null) {
            this.viewer.scene.addMaterial(this.source_material);
        }
        this.raycast_acceleration_structure = build_bvh_from_mesh(this._source_mesh);
    }
    dispose() {
        this._source_key_variant_meshes.forEach((mesh_variant) => {
            if (mesh_variant.hasInstances) {
                throw new Error('Mesh Variant disposed while still having instances');
            }
            mesh_variant.dispose(true, false);
        });
        this._source_key_variant_meshes.clear();
        // Do not dispose this._source_mesh, as its owned by our SceneAssetContainer
        this._custom_geometry.forEach((geometry) => {
            if (geometry !== null && !geometry.isDisposed()) {
                geometry.dispose();
            }
        });
        this._custom_geometry.clear();
    }
    /**
     * Will delete the variant source used by the instance if it no longer contains any instances.
     * Should be called after having removed 'instance.babylon_instance' from the list of instances in the given source.
     * The 'source' parameter is thus given manually, as we can't fetch the source mesh from the instance at that point.
     */
    dispose_unused_variant_source(instance, source) {
        if (!source.hasInstances) {
            // Find the source mesh key that this source was created as
            const existing_source_mesh_key = this._instance_to_active_mesh_key.get(instance);
            if (existing_source_mesh_key === undefined) {
                throw new Error('Invalid internal state: instance mesh key is not registered');
            }
            const existing_source = this._source_key_variant_meshes.get(existing_source_mesh_key);
            if (existing_source !== source) {
                throw new Error(this._source_mesh.uniqueId + ': Invalid internal state: variant does not exist or does not match the actual source. Given source: ' + source.name + ', found source: ' + (existing_source === null || existing_source === void 0 ? void 0 : existing_source.name));
            }
            if (existing_source !== this._source_mesh) {
                source.dispose(true, false);
                this._source_key_variant_meshes.delete(existing_source_mesh_key);
            }
        }
        else if (source.instances.length === 1 && source.instances[0] === instance.babylon_instance) {
            throw new Error('Function was called during an invalid state: The given instance is still an instance of the given source. Please delete the instance from the source first.');
        }
    }
    instantiate_mesh_instance(instance) {
        // If we will be the only instance, then we can use our source mesh. This is an optimization to not need to create a source mesh clone, which can be expensive.
        const is_only_instance = this._instance_to_active_mesh_key.size === 0;
        const can_use_source_as_source_mesh = is_only_instance && instance.mesh_variant_key.unique_source_vertex_data_id === null;
        const source_mesh = can_use_source_as_source_mesh ? this._source_mesh : this.get_babylon_source_mesh(instance.mesh_variant_key);
        if (can_use_source_as_source_mesh) {
            // Special case for when we use our source mesh
            this._source_key_variant_meshes.set(instance.mesh_variant_key.hash(), source_mesh);
            this.apply_source_mesh_key_to_mesh(instance.mesh_variant_key, this._source_mesh);
        }
        const babylon_instance = source_mesh.createInstance(source_mesh.name + SceneAssetMesh._unique_mesh_id_counter++);
        babylon_instance.parent = null;
        this._instance_to_active_mesh_key.set(instance, instance.mesh_variant_key.hash());
        babylon_instance.instancedBuffers[OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME] = new BABYLON_Vector4(0, 0, 0, 0);
        babylon_instance.instancedBuffers[ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME] = new BABYLON_Vector4(1, 1, 1, 1);
        return babylon_instance;
    }
    update_source_mesh_key_for_instance(instance) {
        if (this._instance_to_active_mesh_key.get(instance) === instance.mesh_variant_key.hash())
            return;
        // If we're the sole instance of our source (which is guaranteed to have been created by us), and the variant we want is not cached, update our existing source instead.
        const try_update_source_in_place = instance.babylon_instance.sourceMesh.instances.length === 1 && !this._source_key_variant_meshes.has(instance.mesh_variant_key.hash());
        // Some in-place updates of source meshes are bad, so with this flag we can block it.
        let can_update_source_in_place = true;
        if (try_update_source_in_place) {
            // Check in-place update compatibility
            // For some reason, can't get this value to update properly in-place. So whenever there's a mismatch, we need to create a new source.
            if (instance.mesh_variant_key.receive_shadows !== instance.babylon_instance.sourceMesh.receiveShadows) {
                can_update_source_in_place = false;
            }
        }
        if (try_update_source_in_place && can_update_source_in_place) {
            if (instance.babylon_instance.sourceMesh.instances[0] !== instance.babylon_instance) {
                throw new Error('Invalid internal state: single source instance does not match our instance');
            }
            // Update the existing source mesh in place
            const existing_source_mesh_key = this._instance_to_active_mesh_key.get(instance);
            if (existing_source_mesh_key === undefined) {
                throw new Error('Invalid internal state: the instance is not registered');
            }
            const existing_source_mesh = instance.babylon_instance.sourceMesh;
            this.apply_source_mesh_key_to_mesh(instance.mesh_variant_key, existing_source_mesh);
            this._source_key_variant_meshes.delete(existing_source_mesh_key);
            this._source_key_variant_meshes.set(instance.mesh_variant_key.hash(), existing_source_mesh);
        }
        else {
            const source = this.get_babylon_source_mesh(instance.mesh_variant_key);
            if (source !== instance.babylon_instance.sourceMesh) {
                const previous_source = instance.babylon_instance.sourceMesh;
                babylon_change_source_mesh(instance.babylon_instance, source);
                this.dispose_unused_variant_source(instance, previous_source);
            }
        }
        this._instance_to_active_mesh_key.set(instance, instance.mesh_variant_key.hash());
    }
    get_babylon_source_mesh(source_mesh_key) {
        var _a;
        const hash = source_mesh_key.hash();
        if (this._source_key_variant_meshes.has(hash)) {
            return this._source_key_variant_meshes.get(hash);
        }
        // This is a lot faster than doing source_mesh.clone.
        const clone = new BABYLON_Mesh('', this._source_mesh._scene, null, null);
        (_a = this._source_mesh.geometry) === null || _a === void 0 ? void 0 : _a.applyToMesh(clone);
        clone.material = this._source_mesh.material;
        // Passing null to the as the parent to the clone function doesn't actually set the parent to null.
        // This causes this clone to show up in the SceneAssetContainer graph which it shouldn't.
        clone.parent = null;
        clone.renderingGroupId = this._source_mesh.renderingGroupId;
        clone.setEnabled(false);
        this.setup_custom_instancing_buffers(clone);
        this._source_key_variant_meshes.set(hash, clone);
        this.apply_source_mesh_key_to_mesh(source_mesh_key, clone);
        return clone;
    }
    apply_source_mesh_key_to_mesh(source_mesh_key, mesh) {
        var _a, _b, _c, _d, _e;
        if (source_mesh_key.custom_material) {
            // Typescript complains about Immutable material here, which is fair, but not sure what's the best solution
            mesh.material = source_mesh_key.custom_material;
        }
        else {
            mesh.material = this.source_material;
        }
        // Custom geometry handling. E.g Cutouts
        if (source_mesh_key.unique_source_vertex_data_id === null) {
            // We don't want custom geometry data
            if (mesh.geometry !== this._source_mesh.geometry) {
                // We had custom geometry, so release it and load our source's mesh data
                (_a = mesh.geometry) === null || _a === void 0 ? void 0 : _a.releaseForMesh(mesh, true);
                (_b = this._source_mesh.geometry) === null || _b === void 0 ? void 0 : _b.applyToMesh(mesh);
            }
        }
        else {
            // Make sure we aren't accidentaly trying to apply custom geometry to our source mesh
            if (mesh === this._source_mesh) {
                throw new Error("Invalid state: custom geometry requested on source mesh's data, which is impossible");
            }
            // We want custom geometry
            const existing_custom_geometry = this._custom_geometry.get(source_mesh_key.unique_source_vertex_data_id);
            if (existing_custom_geometry) {
                // Make sure we haven't accidentaly disposed the existing geometry due to some sort of race condition
                if (existing_custom_geometry.isDisposed()) {
                    throw new Error("Invalid state: existing custom geometry was disposed");
                }
                // The custom geometry we want already exists
                if (mesh.geometry !== existing_custom_geometry) {
                    // It doesn't match the mesh we have, so we need to apply it
                    (_c = mesh.geometry) === null || _c === void 0 ? void 0 : _c.releaseForMesh(mesh, true);
                    existing_custom_geometry.applyToMesh(mesh);
                }
            }
            else {
                // The custom geometry we want doesn't exist yet, meaning it should be a copy of our source meshes data
                (_d = mesh.geometry) === null || _d === void 0 ? void 0 : _d.releaseForMesh(mesh, true);
                (_e = this._source_mesh.geometry) === null || _e === void 0 ? void 0 : _e.applyToMesh(mesh);
                mesh.makeGeometryUnique();
                this._custom_geometry.set(source_mesh_key.unique_source_vertex_data_id, mesh.geometry);
            }
        }
        if (mesh.receiveShadows !== source_mesh_key.receive_shadows) {
            mesh.receiveShadows = source_mesh_key.receive_shadows;
            mesh._resyncLightSources(); // This removes some WebGL errors but it feels like there should be a better function to call (TODO: Recheck 4.2)
        }
        const source_matrix_sign = this._source_mesh._getWorldMatrixDeterminant() >= 0.0;
        const wanted_matrix_sign = source_mesh_key.transform_positive_determinant;
        if (source_matrix_sign !== wanted_matrix_sign) {
            mesh.scaling = new BABYLON_Vector3(-mesh.scaling.x, mesh.scaling.y, mesh.scaling.z);
            // If we don't force recompute the world matrix, the mesh can disappear when dealing with negative determinants
            mesh.computeWorldMatrix(true);
            mesh.markAsDirty('');
        }
    }
    setup_custom_instancing_buffers(mesh) {
        mesh.registerInstancedBuffer(OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME, OutlineLayer.INSTANCE_DATA_ATTRIBUTE_STRIDE);
        mesh.registerInstancedBuffer(ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME, 4);
        mesh.instancedBuffers[OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME] = new BABYLON_Vector4(0, 0, 0, 0);
        mesh.instancedBuffers[ALBEDO_ROUGHNESS_INSTANCE_ATTRIBUTE_NAME] = new BABYLON_Vector4(1, 1, 1, 1);
    }
}
SceneAssetMesh._unique_mesh_id_counter = 0;
//# sourceMappingURL=SceneAssetMesh.js.map